// The game log on a player's card (B-218; "Season so far" until 2026-09-27):
// every week of his season that has started, scored this league's way, the
// weeks before the league's own first among them. PLAN-2026-09-24 item H,
// B-131.
//
// FROM THE CARD'S OWN READ since W8: `PlayerCardView.seasonSoFar` is exactly
// what `…/players/:playerId/weeks` answers, less who holds him, which the card
// says itself from `inLeague`. One read for the card rather than two.
//
// A BOX SCORE, NEWEST FIRST: stat detail, such as a back's rushing and
// passing touchdowns, is what informs a decision. The columns are his
// position's own (its stat sheet, B-517), whatever his numbers, and a played
// week opens to how its points were made, stat by stat, under the league's
// rules. The latest week is on top because it is the one being asked about.
//
// A WEEK WITH NO LINE SAYS WHAT IT WAS. Bye, no stats, not played yet and no
// line yet are the server's states, and each is a word here rather than a
// zero that reads as a bad game.
//
// DETAIL LEVELS: Simple shows each week's opponent and points; Standard adds
// the sheet's Simple and Standard columns, the season's games and average,
// the breakdown under a tapped week, and at the foot the season's totals and
// last season's (B-414); Everything adds the sheet's rates and the rest of
// its columns (DETAIL_LEVELS.md — a level removes numbers, it never adds
// words).
//
// THE TOTALS AND LAST SEASON UNDER THE SAME COLUMNS (B-414), the big sites'
// game-log foot: this season's every column over the weeks the log counts (a
// rate over the season's sums), and last season's in this league's scoring —
// 0 for a stat he had none of then, a dash only for points allowed that no
// game reported. Before the first week, last season stands alone, with its
// own columns: it is the season a manager reads in August.

import { PointsByWeek } from '../../kit/charts';
import type { LastSeasonView, PlayerCardView, PlayerWeekRow } from '@fsp/domain';
import { Fragment, useState } from 'react';
import { useAtLeast, useDetail } from '../../data/detail';
import { TableScroll } from '../../kit/TableScroll';
import { Caption, Stack, Text } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { clubLabel } from '../../sports/registry';
import { columnsAt, statCellText } from './StatsTable';

function opponentOf(w: PlayerWeekRow): string {
  if (w.opponent === null) return '';
  return w.home === false ? `at ${clubLabel(w.opponent)}` : `vs ${clubLabel(w.opponent)}`;
}

/** What a week without points says in their place. */
function noLine(w: PlayerWeekRow): string {
  switch (w.state) {
    case 'bye':
      return 'Bye';
    case 'no-line':
      return 'No stats';
    case 'pending':
      return 'No line yet';
    case 'upcoming':
      return 'Not played yet';
    default:
      return '';
  }
}

/** "Wk 3" from "Week 3"; any other label as it is. */
function shortLabel(label: string): string {
  return label.replace(/^Week /, 'Wk ');
}

function fmtValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function fmtPoints(p: number): string {
  return `${p > 0 ? '+' : ''}${p.toFixed(p % 1 === 0 ? 0 : 1)}`;
}

/** Last season alone (B-414): its line in words, and its totals under its own columns. */
function LastSeason({ last }: { last: LastSeasonView }) {
  const columns = columnsAt(last.columns, useDetail());
  return (
    <>
      <Text tone="secondary" block>
        {`${last.season}: ${last.games} ${last.games === 1 ? 'game' : 'games'} · ${last.points.toFixed(1)} pts · ${last.perGame.toFixed(1)} a game, this league’s scoring.`}
      </Text>
      {columns.length > 0 ? (
        <TableScroll className="fsp-log-wrap">
          <table className="fsp-log">
            <thead>
              <tr>
                <th scope="col">Season</th>
                {columns.map((c) => (
                  <th key={c.key} scope="col" className="fsp-log-num">
                    <Term tip={c.name.endsWith('.') ? c.name : `${c.name}.`}>{c.label}</Term>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{String(last.season)}</th>
                {columns.map((c) => (
                  <td key={c.key} className="fsp-log-num">
                    {statCellText(c, last.totals)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </TableScroll>
      ) : null}
    </>
  );
}

export function SeasonLog({
  season,
  last = null,
}: {
  season: PlayerCardView['seasonSoFar'];
  /** His last season (`PlayerCardView.lastSeason`), its row at the foot. */
  last?: LastSeasonView | null;
}) {
  const standard = useAtLeast('standard');
  const level = useDetail();
  const [open, setOpen] = useState<string | null>(null);

  // A league with no calendar yet has no weeks to show (the card's own read
  // says null there, where `…/weeks` answers 409).
  if (season === null || season.weeks.every((w) => w.state === 'upcoming')) {
    return (
      <Stack gap={1}>
        <Caption>Game log</Caption>
        <Text tone="secondary" block>
          The season has not started.
        </Text>
        {standard && last !== null ? <LastSeason last={last} /> : null}
      </Stack>
    );
  }

  // HIS POSITION'S OWN COLUMNS at the reader's level (B-517), from Standard.
  const columns = standard ? columnsAt(season.columns, level) : [];
  // A week whose game has not kicked off is Coming up's, not the log's: the
  // card said "Not played yet" in one and the kickoff in the other.
  const weeks = season.weeks.filter((w) => w.state !== 'upcoming').reverse();
  const span = 3 + columns.length;
  // Every game the log has, oldest first, a week with no line a gap: the
  // same games the average over them counts.
  const charted = season.weeks
    .filter((w) => w.state !== 'upcoming')
    .map((w) => ({ label: shortLabel(w.label), points: w.state === 'played' ? w.points : null }));
  const firstBefore = weeks.findIndex((w) => w.beforeSeason);
  const anyBefore = firstBefore >= 0 && weeks.some((w) => !w.beforeSeason);
  return (
    <Stack gap={1}>
      <Caption>Game log</Caption>
      {/* HIS FORM AT A LOOK (B-406): the played weeks as bars, oldest on the
          left, over the table that holds every number. From Standard, where
          the log's other numbers start. */}
      {standard && charted.filter((w) => w.points !== null).length >= 2 ? (
        <PointsByWeek weeks={charted} average={season.summary.perGame} />
      ) : null}
      {standard && season.summary.games > 0 ? (
        <Text tone="secondary" block>
          {season.summary.games} {season.summary.games === 1 ? 'game' : 'games'} ·{' '}
          {season.summary.points.toFixed(1)} pts
          {season.summary.perGame === null ? '' : ` · ${season.summary.perGame.toFixed(1)} a game`}
        </Text>
      ) : null}
      <TableScroll className="fsp-log-wrap">
        <table className="fsp-log">
          <thead>
            <tr>
              <th scope="col">Wk</th>
              <th scope="col">Opp</th>
              <th scope="col" className="fsp-log-num">
                Pts
              </th>
              {columns.map((c) => (
                <th key={c.key} scope="col" className="fsp-log-num">
                  {/* The stat's whole name when asked (B-374). */}
                  <Term tip={c.name.endsWith('.') ? c.name : `${c.name}.`}>{c.label}</Term>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w, i) => {
              const played = w.state === 'played' && w.points !== null;
              const opens = standard && played && (w.breakdown?.length ?? 0) > 0;
              const isOpen = opens && open === w.windowId;
              return (
                <Fragment key={w.windowId}>
                  {anyBefore && i === firstBefore ? (
                    <tr className="fsp-log-part">
                      <th scope="colgroup" colSpan={span}>
                        Before this league’s season
                      </th>
                    </tr>
                  ) : null}
                  {/* THE WEEK'S OWN BUTTON OPENS IT, its press layer stretched
                      over the row (UX-2, UX-30, UX-32, the UX run of
                      2026-10-21): `aria-expanded` on a table row is not one a
                      screen reader reads outside a tree grid, so the row said
                      nothing, and a row and a button in it were two targets. */}
                  <tr className={`fsp-log-row${opens ? ' fsp-log-row-opens' : ''}`}>
                    <th scope="row">
                      {opens ? (
                        <button
                          type="button"
                          className="fsp-linklike fsp-log-open"
                          aria-expanded={isOpen}
                          onClick={() => setOpen(isOpen ? null : w.windowId)}
                        >
                          {shortLabel(w.label)}
                        </button>
                      ) : (
                        shortLabel(w.label)
                      )}
                    </th>
                    <td>{opponentOf(w) || '—'}</td>
                    {played ? (
                      <>
                        <td className="fsp-log-num fsp-log-pts">{w.points!.toFixed(1)}</td>
                        {columns.map((c) => (
                          <td key={c.key} className="fsp-log-num">
                            {/* Absent is not 0 for a tiered stat: points
                                allowed the game did not report (B-218). */}
                            {statCellText(c, w.stats)}
                          </td>
                        ))}
                      </>
                    ) : (
                      <td colSpan={span - 2} className="fsp-muted">
                        {noLine(w)}
                      </td>
                    )}
                  </tr>
                  {isOpen ? (
                    <tr className="fsp-log-breakdown">
                      <td colSpan={span}>
                        <div className="fsp-log-items">
                          {w.breakdown!.map((b) => (
                            <div key={`${b.stat}-${b.label}`} className="fsp-log-item">
                              <span>
                                {b.label} <span className="fsp-muted">{fmtValue(b.value)}</span>
                              </span>
                              <span className="fsp-num">{fmtPoints(b.points)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
          {standard && (season.summary.games > 0 || last !== null) ? (
            <tfoot>
              {season.summary.games > 0 ? (
                <tr className="fsp-log-total">
                  <th scope="row">Total</th>
                  <td>{`${season.summary.games} G`}</td>
                  <td className="fsp-log-num fsp-log-pts">{season.summary.points.toFixed(1)}</td>
                  {columns.map((c) => (
                    <td key={c.key} className="fsp-log-num">
                      {statCellText(c, season.summary.totals)}
                    </td>
                  ))}
                </tr>
              ) : null}
              {last !== null ? (
                <tr className="fsp-log-total fsp-log-last">
                  <th scope="row">
                    <Term tip={`His ${last.season} season, in this league’s scoring.`}>
                      {String(last.season)}
                    </Term>
                  </th>
                  <td>{`${last.games} G`}</td>
                  <td className="fsp-log-num fsp-log-pts">{last.points.toFixed(1)}</td>
                  {columns.map((c) => (
                    <td key={c.key} className="fsp-log-num">
                      {statCellText(c, last.totals)}
                    </td>
                  ))}
                </tr>
              ) : null}
            </tfoot>
          ) : null}
        </table>
      </TableScroll>
      {standard && last !== null ? (
        <Text tone="secondary" block>
          {`${last.season}: ${last.games} ${last.games === 1 ? 'game' : 'games'} · ${last.points.toFixed(1)} pts · ${last.perGame.toFixed(1)} a game, this league’s scoring.`}
        </Text>
      ) : null}
      {standard && weeks.some((w) => w.state === 'played' && (w.breakdown?.length ?? 0) > 0) ? (
        <Text tone="secondary" block>
          Tap a week for how its points were made.
        </Text>
      ) : null}
    </Stack>
  );
}
